import React, { useState } from 'react';
import { Button, InputNumber, Modal } from 'antd';

import {
  BatchCreateCreditLogsResponse,
  Client,
} from '@cbinsights/externalapiservice/externalapiservice';
import { useAddCredits } from 'client/modules/external-api/hooks/queries/external-api';
import { useQueryClient } from 'react-query';

export const useAddCreditsInput = (): {
  amount: number;
  content: React.ReactNode;
  error: Error;
  isLoading: boolean;
  reset: () => void;
  submit: (clientId: string) => Promise<BatchCreateCreditLogsResponse | void>;
} => {
  const [amount, setAmount] = useState(0);
  const {
    error,
    isLoading,
    mutateAsync,
    reset: resetMutation,
  } = useAddCredits();

  const reset = () => {
    setAmount(0);
    resetMutation();
  };

  const submit = (clientId: string) =>
    amount > 0 ? mutateAsync({ clientId, amount }) : Promise.resolve();

  const content = (
    <InputNumber
      addonBefore="Amount"
      autoFocus
      min={0}
      max={100000000}
      onChange={(value) => setAmount(Number(value) || 0)}
      step={1000}
      value={amount}
    />
  );

  return {
    amount,
    content,
    error,
    isLoading,
    reset,
    submit,
  };
};

export const AddCreditsButton = ({
  className,
  client,
}: {
  className?: string;
  client: Client;
}): React.ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const idTeam = Number(client.name);
  const availableCredits = Number(client.available_credits);
  const closeModal = () => setIsModalOpen(false);

  const { amount, content, error, isLoading, reset, submit } =
    useAddCreditsInput();

  const saveCredits = async () => {
    await submit(client.client_id);
    queryClient.invalidateQueries([
      'externalapiservice/ListTeamClients',
      idTeam,
    ]);
    closeModal();
  };

  return (
    <>
      <Button
        className={className}
        onClick={() => {
          reset();
          setIsModalOpen(true);
        }}
        type="primary"
      >
        Add Credits
      </Button>
      {isModalOpen && (
        <Modal
          title="Add Credits"
          okButtonProps={{
            disabled: isLoading || amount === 0,
            loading: isLoading,
          }}
          onOk={saveCredits}
          onCancel={() => {
            if (!isLoading) closeModal();
          }}
          visible
        >
          {content}
          <p className="mt-4">
            <span className="font-medium">Current Balance: </span>
            {availableCredits}
          </p>
          {amount > 0 && (
            <p className="text-green-600">
              <span className="font-medium">New Balance: </span>
              {availableCredits + amount}
            </p>
          )}
          {error && (
            <p className="text-red-600 text-sm mt-2">
              An error occurred while updating credits. Please try again.
            </p>
          )}
        </Modal>
      )}
    </>
  );
};

export default AddCreditsButton;
